import PageNavigation from "../components/navigation/pageNavigation";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useEffect, useState } from "react";
import {
  CheckIcon,
  ExclamationIcon,
  MailIcon,
  PhoneIcon,
} from "@heroicons/react/solid";
import PageFooter from "@components/navigation/pageFooter";
import Image from "next/image";
import PageHeader from "@components/pageHeader";

const ContactPage = ({ contact }) => {
  const [mode, setMode] = useState("light");
  const [formData, updateFormData] = useState({});
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation("contact");

  const toBase64 = async (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const updateFormError = (error) => {
    setFormError(error);
    location.hash = "";
    if (!error) {
      location.hash = "#formError";
    }
  };

  /**
   * Detect if the user is on a dark mode browser.
   * Used for displaying captcha in correct theme.
   */
  useEffect(() => {
    // Add listener to update styles
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (e) => setMode(e.matches ? "dark" : "light"));

    // Setup dark/light mode for the first time
    setMode(
      window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
    );

    // Remove listener
    return () => {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", () => {});
    };
  }, []);

  /**
   * Generates a form field with the given props
   */
  const createInput = (
    name,
    id,
    autocomplete = undefined,
    type = "text",
    required = true,
    textarea = false,
    multiple = false
  ) => {
    const inputClasses =
      "block w-full sm:text-sm border-[0] focus:ring-default focus:ring-2 rounded dark:bg-neutral-800 focus:outline-none file:py-2 file:px-4 file:bg-default file:text-white file:mr-4 file:border-none";

    return (
      <>
        <div>
          <label
            htmlFor={id}
            className="flex justify-between text-sm font-medium text-neutral-700 dark:text-white"
          >
            <p>
              {t(name)}
              <span className="inline-block text-red-500">
                {required && "*"}
              </span>
            </p>
            {!required && (
              <span className="text-sm text-neutral-500 dark:text-neutral-300">
                {t("form.optional")}
              </span>
            )}
          </label>
          <div
            className={
              "mt-1 border-neutral-300 dark:border-neutral-700 border-[1px] rounded shadow-sm focus:!border-red-500"
            }
          >
            {!textarea ? (
              <input
                id={id}
                name={name}
                type={type}
                autoComplete={autocomplete}
                className={inputClasses}
                required={required}
                onChange={handleChange}
                multiple={multiple}
              />
            ) : (
              <textarea
                id={id}
                name={name}
                rows={4}
                className={inputClasses}
                required={required}
                onChange={handleChange}
              />
            )}
          </div>
        </div>
      </>
    );
  };

  /**
   * Transforms form inputs into an object (state)
   */
  const handleChange = (e) => {
    if (e.target.name === "form.phone") {
      e.target.value = e.target.value.replace(/\s/g, "");
    }

    updateFormData({
      ...formData,
      ...{
        [e.target.id]:
          e.target.type === "file" ? e.target.files : e.target.value.trim(),
      },
    });
  };

  /**
   * Handle form submission and validation before sending to the server
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    // reset the error message
    updateFormError(null);

    if (loading) {
      updateFormError(t("validation.wait"));
      return;
    }

    // create a local copy of form data we can work with
    const data = formData;

    // make sure that the first-name is filled out
    if (!data.firstName) {
      updateFormError(t("validation.missingName"));
      return;
    }

    // make sure that the last-name is filled out
    if (!data.lastName) {
      updateFormError(t("validation.missingLastName"));
      return;
    }

    // make sure that the email is filled out
    if (!data.email) {
      updateFormError(t("validation.missingEmail"));
      return;
    }

    // make sure that the message is filled out and not longer that 500 characters
    if (!data.message || data.message.length > 500) {
      updateFormError(t("validation.missingMessage"));
      return;
    }

    // make sure that the email address is valid
    if (data.email && !/^[^@]+@[^@.]+\.[^@.]+$/.test(data.email)) {
      updateFormError(t("validation.invalidEmail"));
      return;
    }

    // if the phone is presetn, validate it
    if (data.phone && !/^\+?[0-9]{10,15}$/.test(data.phone)) {
      updateFormError(t("validation.invalidPhone"));
      return;
    }

    if (data.files && data.files.length > 0) {
      //make sure that each file is less than 5MB
      for (let i = 0; i < data.files.length; i++) {
        if (data.files[i].size > 5000000) {
          updateFormError(t("validation.fileSize"));
          return;
        }
      }
    }

    if (!data.hcaptcha) {
      updateFormError(t("validation.captcha"));
      return;
    }

    submit(data);
  };

  const submit = async (data) => {
    setLoading(true);
    let attachments = [];

    if (data.files) {
      for (let i = 0; i < data.files.length; i++) {
        const file = data.files[i];

        // covert the file to a base64 string
        const base64 = await toBase64(file);

        attachments.push({
          Name: file.name,
          Content: base64.substr(base64.indexOf(",") + 1), // remove base64 header
          ContentType: file.type,
        });
      }

      data["attachments"] = attachments;
    }

    fetch("/api/form", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => {
        if (res.status === 200) {
          updateFormError({
            message: t("messageSuccess"),
            success: true,
          });
          updateFormData({});
          // clear the contact form
          document.getElementById("contact-form").innerHTML = "";
        } else {
          console.error(res);
          updateFormError(
            `${t("messageError")} (${res.status} ${res.statusText})`
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <PageNavigation />
      <PageHeader
        title={t("header.title")}
        description={null}
        classes="!pb-[7.5rem]"
      />
      <div className="grid grid-cols-1 gap-8 mt-5 text-white transform -translate-y-28 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 c-container">
        {contact &&
          contact.data.map((item) => {
            return (
              <div key={item.id} className="px-6 py-4 rounded bg-default">
                <span className="text-lg font-semibold">
                  {item.attributes.fullName}
                </span>
                <p>{item.attributes.role}</p>
                <article className="mt-4">
                  <a
                    href={
                      "tel:" +
                      item.attributes.mobile
                        .replace(/\s/g, "")
                        .replace("+", "00")
                    }
                    className="flex items-center py-1"
                  >
                    <PhoneIcon className="w-5 h-5" />
                    <p className="ml-2">{item.attributes.mobile}</p>
                  </a>
                  <a
                    href={`mailto:${item.attributes.mail}`}
                    className="flex items-center py-1"
                  >
                    <MailIcon className="w-5 h-5" />
                    <p className="ml-2">{item.attributes.mail}</p>
                  </a>
                </article>
              </div>
            );
          })}
      </div>
      <main className="c-container -mt-28">
        <header>
          <h1 className="text-4xl font-semibold">{t("contactForm")}</h1>
        </header>
        <div>
          <div className="mx-auto">
            <div id="formError">
              {formError && (
                <div
                  className={
                    "p-4 border-2 border-red-500 text-red-500 rounded flex items-center mt-6 " +
                    (formError?.success && "border-green-500 text-green-500")
                  }
                >
                  {formError?.success ? (
                    <CheckIcon className="w-5 h-5" />
                  ) : (
                    <ExclamationIcon className="w-5 h-5" />
                  )}
                  <p className="ml-2">{formError?.message || formError}</p>
                </div>
              )}
              <form
                action="#"
                method="POST"
                className="grid grid-cols-1 mt-9 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
                id="contact-form"
              >
                {createInput("form.name", "firstName", "given-name")}
                {createInput("form.lastName", "lastName", "family-name")}
                <div className="sm:col-span-2">
                  {createInput("form.email", "email", "email", "email")}
                </div>
                <div className="sm:col-span-2">
                  {createInput("form.phone", "phone", "tel", "tel", false)}
                </div>
                <div className="sm:col-span-2">
                  {createInput(
                    "form.message",
                    "message",
                    null,
                    null,
                    true,
                    true
                  )}
                </div>
                <div className="sm:col-span-2">
                  {createInput(
                    "form.attachments",
                    "files",
                    null,
                    "file",
                    false,
                    false,
                    true
                  )}
                </div>
                <HCaptcha
                  sitekey={`7ee67dbb-97df-47e9-81a4-8ffe3b5869e2`}
                  onVerify={(token, ekey) =>
                    updateFormData({
                      ...formData,
                      ...{
                        ["hcaptcha"]: { token, ekey },
                      },
                    })
                  }
                  theme={mode}
                />
                <div className="text-right sm:col-span-2">
                  <button
                    type="submit"
                    className="button button-blue"
                    onClick={handleSubmit}
                  >
                    {t("form.send")}
                  </button>
                </div>
              </form>
            </div>
            {/* <div>
              <div className="p-6 text-lg border-2 rounded shadow border-default">
                <p>
                  <strong className="font-semibold">Billing details</strong>
                  <br />
                  Lptex, s.r.o.
                  <br />
                  Južná trieda 119
                  <br />
                  Košice 040 01
                  <br />
                  Slovakia
                  <br />
                  <br />
                  <strong className="font-semibold">Company ID:&nbsp;</strong>
                  36576361
                  <br />
                  <strong className="font-semibold">VAT ID:&nbsp;</strong>
                  SK2021778715
                  <br />
                  <strong className="font-semibold">Bank&nbsp;</strong>
                  Slovenská sporiteľňa, a.s.
                  <br />
                  <strong className="font-semibold">IBAN:&nbsp;</strong>
                  SK1109000000000445493645
                  <br />
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </main>
      <PageFooter />
    </>
  );
};

export default ContactPage;

export async function getStaticProps({ locale }) {
  const baseUrl = process.env.STRAPI_URL;

  try {
    const responseContact = await fetch(
      `${baseUrl}/api/contacts?locale=${locale}`,
      {
        headers: {
          Authorization: `Bearer ${process.env.STRAPI_KEY}`,
        },
      }
    );

    const contact = await responseContact.json();

    return {
      props: {
        contact,
        baseUrl,
        ...(await serverSideTranslations(locale, ["common", "contact"])),
      },
      revalidate: 60,
    };
  } catch (error) {
    console.log(error);

    return null;
  }
}
